import { useState, useEffect, useMemo, useCallback } from "react";
import { Sidebar, Menu, MenuItem, SubMenu } from "react-pro-sidebar";
import { Box, IconButton, Typography, Tooltip, useTheme, alpha } from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { tokens } from "../../theme";
import { useAuth } from "../../AuthContext";

import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import MenuOpenOutlinedIcon from "@mui/icons-material/MenuOpenOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import WallpaperOutlinedIcon from "@mui/icons-material/WallpaperOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import EmojiObjectsOutlinedIcon from "@mui/icons-material/EmojiObjectsOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import AnnouncementOutlinedIcon from "@mui/icons-material/AnnouncementOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import NewspaperOutlinedIcon from "@mui/icons-material/NewspaperOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import RssFeedOutlinedIcon from "@mui/icons-material/RssFeedOutlined";

/**
 * Website content grouped by purpose.
 * Add or reorder here — menu, active state and auto-expand all follow.
 */
const NAV_GROUPS = [
  {
    id: "setup",
    label: "Site Setup",
    icon: <SettingsOutlinedIcon />,
    items: [
      {
        title: "Company Details",
        to: "/website/dashboard/settings",
        icon: <BusinessOutlinedIcon />,
      },
      {
        title: "Backgrounds",
        to: "/website/dashboard/backgrounds",
        icon: <WallpaperOutlinedIcon />,
      },
      {
        title: "Public Holidays",
        to: "/website/dashboard/public-holidays",
        icon: <CalendarTodayOutlinedIcon />,
      },
    ],
  },
  {
    id: "announcements",
    label: "Announcements",
    icon: <CampaignOutlinedIcon />,
    items: [
      {
        title: "Top Header Ads",
        to: "/website/dashboard/announcements",
        icon: <AnnouncementOutlinedIcon />,
      },
      {
        title: "Special",
        to: "/website/dashboard/special",
        icon: <LocalOfferOutlinedIcon />,
      },
      {
        title: "Adverts",
        to: "/website/dashboard/adverts",
        icon: <CampaignOutlinedIcon />,
      },
    ],
  },
  {
    id: "organisation",
    label: "Organisation",
    icon: <ApartmentOutlinedIcon />,
    items: [
      {
        title: "About Us",
        to: "/website/dashboard/about-us",
        icon: <InfoOutlinedIcon />,
      },
      {
        title: "Departments",
        to: "/website/dashboard/departments",
        icon: <AccountTreeOutlinedIcon />,
      },
      {
        title: "Services",
        to: "/website/dashboard/services",
        icon: <BuildOutlinedIcon />,
      },
      {
        title: "Our Clients",
        to: "/website/dashboard/clients",
        icon: <GroupOutlinedIcon />,
      },
      {
        title: "Our Partners",
        to: "/website/dashboard/partners",
        icon: <PeopleAltOutlinedIcon />,
      },
      {
        title: "Testimonials",
        to: "/website/dashboard/testimonials",
        icon: <RateReviewOutlinedIcon />,
      },
    ],
  },
  {
    id: "programmes",
    label: "Programmes & Projects",
    icon: <ScienceOutlinedIcon />,
    items: [
      {
        title: "Projects",
        to: "/website/dashboard/projects",
        icon: <AssignmentOutlinedIcon />,
      },
      {
        title: "Modules",
        to: "/website/dashboard/modules",
        icon: <ExtensionOutlinedIcon />,
      },
      {
        title: "Why STEAM (Stats)",
        to: "/website/dashboard/why-steam",
        icon: <TrendingUpOutlinedIcon />,
      },
      {
        title: "STEAM Project",
        to: "/website/dashboard/steam/project",
        icon: <ScienceOutlinedIcon />,
      },
      {
        title: "CSR Projects",
        to: "/website/dashboard/csr",
        icon: <HandshakeOutlinedIcon />,
      },
      {
        title: "CSR Activities",
        to: "/website/dashboard/csr-activity",
        icon: <EmojiObjectsOutlinedIcon />,
      },
    ],
  },
  {
    id: "publishing",
    label: "Publishing",
    icon: <NewspaperOutlinedIcon />,
    items: [
      {
        title: "Events",
        to: "/website/dashboard/events",
        icon: <EventOutlinedIcon />,
      },
      {
        title: "News",
        to: "/website/dashboard/news",
        icon: <ArticleOutlinedIcon />,
      },
      {
        title: "Our Blogs",
        to: "/website/dashboard/blog",
        icon: <RssFeedOutlinedIcon />,
      },
      {
        title: "Newsletters",
        to: "/website/dashboard/newsletter",
        icon: <EmailOutlinedIcon />,
      },
      {
        title: "Our Gallery",
        to: "/website/dashboard/gallery",
        icon: <PhotoLibraryOutlinedIcon />,
      },
    ],
  },
];

const WebsiteSideBar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const location = useLocation();
  const navigate = useNavigate();
  const { userRole } = useAuth();

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [openGroups, setOpenGroups] = useState({});

  /* ---------------- Derived design tokens ---------------- */
  const ui = useMemo(() => {
    const accent = colors.teal[500];
    const accent2 = colors.newTeal[500];
    return {
      accent,
      accent2,
      surface: isDark ? colors.dark[700] : colors.white[900],
      surfaceAlt: isDark ? colors.dark[600] : colors.white[600],
      border: isDark
        ? alpha(colors.white[500], 0.09)
        : alpha(colors.dark[500], 0.1),
      textPrimary: colors.primary[100],
      textSecondary: isDark ? colors.gray[400] : colors.gray[300],
      gradient: `linear-gradient(135deg, ${accent} 0%, ${accent2} 100%)`,
    };
  }, [colors, isDark]);

  /* ---------------- Active route ---------------- */
  const isActive = useCallback(
    (to) => location.pathname === to || location.pathname.startsWith(`${to}/`),
    [location.pathname]
  );

  const activeGroupId = useMemo(
    () => NAV_GROUPS.find((g) => g.items.some((i) => isActive(i.to)))?.id || null,
    [isActive]
  );

  // Expand whichever group owns the current route.
  useEffect(() => {
    if (activeGroupId) {
      setOpenGroups((prev) => ({ ...prev, [activeGroupId]: true }));
    }
  }, [activeGroupId]);

  const toggleGroup = (id, open) =>
    setOpenGroups((prev) => ({ ...prev, [id]: open }));

  /* ---------------- react-pro-sidebar styling ---------------- */
  const menuItemStyles = {
    root: {
      fontSize: 13.5,
      fontWeight: 600,
    },
    icon: ({ active }) => ({
      color: active ? ui.accent : ui.textSecondary,
      marginRight: isCollapsed ? 0 : 10,
      transition: "color .18s ease",
    }),
    SubMenuExpandIcon: {
      color: ui.textSecondary,
    },
    subMenuContent: {
      backgroundColor: isCollapsed ? ui.surface : "transparent",
      borderRadius: isCollapsed ? 10 : 0,
    },
    button: ({ level, active }) => ({
      height: 42,
      margin: "2px 10px",
      borderRadius: 10,
      paddingLeft: level === 0 ? 14 : isCollapsed ? 14 : 26,
      color: active ? ui.accent : ui.textSecondary,
      backgroundColor: active ? alpha(ui.accent, isDark ? 0.16 : 0.12) : "transparent",
      transition: "background-color .18s ease, color .18s ease",
      "&:hover": {
        color: ui.accent,
        backgroundColor: alpha(ui.accent, isDark ? 0.12 : 0.09),
      },
    }),
    label: {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  };

  const SectionLabel = ({ children }) =>
    isCollapsed ? (
      <Box sx={{ height: 14 }} />
    ) : (
      <Typography
        variant="h6"
        sx={{
          m: "16px 0 6px 24px",
          fontSize: 10.5,
          fontWeight: 800,
          letterSpacing: 1.1,
          textTransform: "uppercase",
          color: alpha(ui.textSecondary, 0.85),
        }}
      >
        {children}
      </Typography>
    );

  return (
    <Sidebar
      collapsed={isCollapsed}
      width="272px"
      collapsedWidth="82px"
      rootStyles={{
        height: "100vh",
        border: "none",
        borderRight: `1px solid ${ui.border}`,
        "& .ps-sidebar-container": {
          background: ui.surface,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          overflowX: "hidden",
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            background: alpha(ui.textSecondary, 0.3),
            borderRadius: 3,
          },
        },
      }}
    >
      {/* ---------------------------------------------------------------- */}
      {/* Header                                                            */}
      {/* ---------------------------------------------------------------- */}
      <Box
        sx={{
          px: isCollapsed ? 0 : 2.25,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: isCollapsed ? "center" : "space-between",
          borderBottom: `1px solid ${ui.border}`,
        }}
      >
        {!isCollapsed && (
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h3"
              noWrap
              sx={{
                fontWeight: 800,
                letterSpacing: -0.3,
                background: ui.gradient,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              WEBSITE
            </Typography>
            <Typography variant="h6" sx={{ fontSize: 11, color: ui.textSecondary }}>
              {userRole === "admin" ? "Administrator" : "Content manager"}
            </Typography>
          </Box>
        )}

        <Tooltip title={isCollapsed ? "Expand menu" : "Collapse menu"} placement="right">
          <IconButton
            onClick={() => setIsCollapsed((prev) => !prev)}
            sx={{
              borderRadius: 2,
              color: ui.textSecondary,
              border: `1px solid ${ui.border}`,
              "&:hover": {
                color: ui.accent,
                borderColor: alpha(ui.accent, 0.5),
                bgcolor: alpha(ui.accent, 0.08),
              },
            }}
          >
            {isCollapsed ? (
              <MenuOutlinedIcon fontSize="small" />
            ) : (
              <MenuOpenOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* ---------------------------------------------------------------- */}
      {/* Menu                                                              */}
      {/* ---------------------------------------------------------------- */}
      <Menu menuItemStyles={menuItemStyles} closeOnClick>
        {userRole !== "receptionist" && (
          <MenuItem icon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
            Go back
          </MenuItem>
        )}

        <MenuItem
          icon={<HomeOutlinedIcon />}
          active={isActive("/website/dashboard")}
          component={<Link to="/website/dashboard" />}
        >
          Dashboard
        </MenuItem>

        <SectionLabel>Website Content</SectionLabel>

        {NAV_GROUPS.map((group) => (
          <SubMenu
            key={group.id}
            label={group.label}
            icon={group.icon}
            open={!!openGroups[group.id]}
            onOpenChange={(open) => toggleGroup(group.id, open)}
            // Keep the parent tinted when it owns the current page but is closed,
            // so the active route is never completely hidden.
            rootStyles={
              group.id === activeGroupId && !openGroups[group.id]
                ? { "& > .ps-menu-button": { color: `${ui.accent} !important` } }
                : undefined
            }
          >
            {group.items.map((item) => (
              <MenuItem
                key={item.to}
                icon={item.icon}
                active={isActive(item.to)}
                component={<Link to={item.to} />}
              >
                {item.title}
              </MenuItem>
            ))}
          </SubMenu>
        ))}
      </Menu>

      <Box sx={{ flex: 1 }} />

      {!isCollapsed && (
        <Box sx={{ px: 2.5, py: 2, borderTop: `1px solid ${ui.border}` }}>
          <Typography variant="h6" sx={{ fontSize: 10.5, color: ui.textSecondary }}>
            © {new Date().getFullYear()} Mindsinaction
          </Typography>
        </Box>
      )}
    </Sidebar>
  );
};

export default WebsiteSideBar;